import React, { useContext, useEffect, useState } from 'react';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import playbackIcon from '@amzn/meridian-tokens/base/icon/play';
import Modal from '@amzn/meridian/modal';
import DataSource from '../../../api';
import { getDateRangeFromTimestamp } from '../../../Util';
import { StateContext } from '../../../GlobalStateStore';
import DataState from '../../../Types/DataState';
import {
  ContactData,
  defaultContactData,
} from '../../../api/ContactListening/schema';
import { BaseContact } from '../../../api/CoachingNotes/schema';
import ContactListeningContent from '../../ContactListening/ContactListeningContent';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import useCustomTracking from '../../../Util/useCustomTracking';
import { CLClickstream } from '../../ContactListening/utils/ContactListeningHelpers';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { getCscEntityPrefix } from '../../../config/Config';
import Column from '@amzn/meridian/column';
import ContactListeningMultitaskingBox from '../../ContactListening/ContactListeningMultitaskingBox';

interface PlaybackModalProps {
  parentValue: BaseContact | undefined;
  CLClickstream: CLClickstream;
  setContactListeningParams: any;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
  showButton?: boolean; // defaults to true
}

const PlaybackModal: React.FC<PlaybackModalProps> = ({
  parentValue,
  CLClickstream,
  setContactListeningParams,
  isOpen,
  onToggle,
  showButton = true,
}: PlaybackModalProps) => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { userID, requesterOwner, requesterUsername, isTm } =
    inspireOptions.data;
  const contactListeningWithChatEnabled = isFeatureEnabled(
    Feature.CONTACT_LISTENING_WITH_CHAT
  );

  const contactListeningMultitaskEnabled = isFeatureEnabled(
    Feature.CONTACT_LISTENING_MULTITASK
  );
  const routeParams = useParams();

  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [contactListeningDataState, setContactListeningDataState] =
    useState<string>(DataState.INITIALIZED);
  const [queryParams, setQueryParams] = useState();

  // Internal state for when modal manages its own state (backwards compatibility)
  const [internalOpen, setInternalOpen] = useState(false);

  // Auto-detect external vs internal control: if both isOpen and onToggle are provided → external control
  const isExternallyControlled = isOpen !== undefined && onToggle !== undefined;

  // Use external state when available, otherwise fall back to internal state
  const modalOpen = isExternallyControlled ? isOpen : internalOpen;

  const [OpenCLModalTrackingTag, openCLModalClick] = useCustomTracking(
    `contact-listening-${parentValue?.contactID}`,
    CLClickstream.slotID,
    {
      'data-csa-c-type': 'button',
      'data-csa-c-tag1': CLClickstream.tag1,
      'data-csa-c-tag2': CLClickstream.tag2,
      'data-csa-c-tag3': CLClickstream.tag3,
      'data-csa-c-tag4': CLClickstream.tag4,
      'data-csa-c-tag5': CLClickstream.tag5,
      'data-csa-c-contactid': parentValue?.contactID,
      'data-csa-c-is-tm': isTm,
      'data-csa-c-username': requesterUsername,
    }
  );

  const contactListeningContentColumn = (
    <Column alignmentHorizontal='stretch'>
      <ContactListeningContent
        contactListeningData={contactListeningData}
        loadingState={contactListeningDataState}
        contactListeningParams={queryParams}
        CLClickstream={{
          ...CLClickstream,
          contactID: parentValue?.contactID,
        }}
      />
    </Column>
  );

  const onClickButton = () => {
    if (isExternallyControlled && onToggle) {
      onToggle(true);
    } else {
      setInternalOpen(true);
    }
  };

  const onClose = () => {
    if (isExternallyControlled && onToggle) {
      onToggle(false);
    } else {
      setInternalOpen(false);
    }
  };
  const intl = useIntl();

  const { contactStartDate, contactEndDate } = parentValue
    ? getDateRangeFromTimestamp(
        parentValue.contactDate,
        parentValue.contactDate
      )
    : getDateRangeFromTimestamp();

  useEffect(() => {
    if (!parentValue || userID === '' || !modalOpen) return;

    openCLModalClick();
    const routeUserID = routeParams?.userId;

    const subject = `${requesterUsername}@${requesterOwner.toLowerCase()}`;

    const contactHandlerId =
      getCscEntityPrefix() + ':' + (routeUserID ? routeUserID : userID);

    const queryVariables = apiNaws.prepareContactListeningDataQuery(
      contactHandlerId,
      parentValue?.contactID,
      contactStartDate,
      contactEndDate,
      subject
    );
    setContactListeningParams(queryVariables);
    setQueryParams(queryVariables);
    if (contactListeningWithChatEnabled) {
      apiNaws.getContactListeningDataWithChat(
        queryVariables,
        setContactListeningData,
        setContactListeningDataState
      );
    } else {
      apiNaws.getContactListeningData(
        queryVariables,
        setContactListeningData,
        setContactListeningDataState
      );
    }
  }, [parentValue, inspireOptions.data, modalOpen]);

  if (parentValue === undefined) {
    return <></>;
  }

  return (
    <>
      {showButton && (
        <Button data-cy='playback-button' type='icon' onClick={onClickButton}>
          <Icon tokens={playbackIcon} />
        </Button>
      )}
      {contactListeningMultitaskEnabled ? (
        <>
          {modalOpen && (
            <ContactListeningMultitaskingBox
              contactId={parentValue?.contactID}
              onClose={() => onClose()}
              column={contactListeningContentColumn}
              headerMsg={`${intl.formatMessage(
                {
                  id: 'cs_perfportal_contact_recom_modal_title_for_contacID',
                  defaultMessage: 'New review for contact {contactID}',
                },
                {
                  contactID: parentValue?.contactID,
                }
              )}`}
              loadingState={contactListeningDataState}
              contactListeningData={contactListeningData}
              openContactTranscript={true}
              setOpenContactTranscript={() => {
                return;
              }}
            ></ContactListeningMultitaskingBox>
          )}
        </>
      ) : (
        <>
          <Modal
            title={intl.formatMessage(
              {
                id: 'cs_perfportal_contact_recom_modal_title_for_contacID',
                defaultMessage: 'New review for contact {contactID}',
              },
              {
                contactID: parentValue?.contactID,
              }
            )}
            open={modalOpen}
            onClose={onClose}
            scrollContainer='viewport'
            closeLabel='Close'
            aria-describedby='modal-description'
          >
            {contactListeningContentColumn}
          </Modal>
        </>
      )}
      <OpenCLModalTrackingTag />
    </>
  );
};

export default PlaybackModal;
